import { HTMLAttributes, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import Link from 'next/link'

import Button from '@/components/base/Button'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'
import { useLenis } from '@/contexts/lenis'
import { useLoaderStore } from '@/store/loader'

import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type HeroButtonProps = HTMLAttributes<HTMLElement> &
  Partial<{
    button: WPLink
  }>

const HeroButton = ({ button, className, ...props }: HeroButtonProps) => {
  const wrapper = useRef<HTMLDivElement>(null!)
  const loaded = useLoaderStore((state) => state.pageLoaded)
  const { lenis } = useLenis()

  useGSAP(
    () => {
      if (!lenis || !loaded) return
      const hero = document.querySelector('[data-hero]')

      gsap.set(wrapper.current, {
        x: '50%',
        right: '50%'
      })

      const triggerHero = ScrollTrigger.create({
        trigger: hero,
        start: 'bottom 80%',
        onEnter: () => {
          gsap.to(wrapper.current, {
            overwrite: true,
            x: '0%',
            right: 20,
            duration: 1,
            ease: 'power2.out'
          })
        },
        onLeaveBack: () => {
          gsap.to(wrapper.current, {
            overwrite: true,
            x: '50%',
            right: '50%',
            duration: 1,
            ease: 'power2.out'
          })
        }
      })

      return () => {
        if (triggerHero) triggerHero.kill()
      }
    },
    {
      dependencies: [lenis, loaded],
      scope: wrapper,
      revertOnUpdate: true
    }
  )

  return (
    <div
      ref={wrapper}
      data-hero-button
      {...props}
      className={cn('mix-blend-exclusion', className)}
    >
      <Button
        type="button"
        variant="dark"
        icon={
          <SVGIcon
            symbol="chevron-right-icon"
            className="h-[11px] w-[7px] shrink-0 text-current"
          />
        }
        asChild
      >
        <Link
          href={button?.url || '/'}
          target={button?.target}
          data-label={button?.title}
          scroll={false}
        >
          <Typography variant="label">{button?.title}</Typography>
        </Link>
      </Button>
    </div>
  )
}

export default HeroButton
